import { NotDisclosed } from "@platform/openapi-types";

import type { TypedBenefitObject } from "./types";

/**
 * Type guard to check if a benefit type value should be included in the API payload.
 * Returns true for all defined values (including NotDisclosed).
 * Returns false only for null/undefined/empty string.
 */
export function shouldIncludeBenefitInApi<T>(
  typeValue: T | undefined | null,
): typeValue is T {
  return typeValue !== undefined && typeValue != null && typeValue !== "";
}

/**
 * Stringifies a grid value bound for a backend field typed as a Go string
 * (e.g. an extended field edited through a numeric cell, whose value AG Grid
 * commits as a number). Leaves unset values unset so `hasAnySet`-style
 * omission checks upstream still work.
 */
export function toExtendedStringField(
  value: string | number | null | undefined,
): string | undefined {
  return value === undefined || value === null || value === ""
    ? undefined
    : String(value);
}

/**
 * Transforms a type/amount pair from grid format to API format.
 *
 * Returns `undefined` when typeValue is missing, or when the type requires an
 * amount but none is provided.
 *
 * @param typeValue - The type field value (e.g., "dollarAmount", "notIncluded")
 * @param amountValue - The amount field value
 * @param includeAmountWhen - Type values that should include the amount (default: ["dollarAmount"])
 * @param amountFieldName - The key name for the amount field (default: "amount")
 */
export function transformTypeAmountToApi(
  typeValue: string | null | undefined,
  amountValue: number | null | undefined,
  includeAmountWhen: string[] = ["dollarAmount"],
  amountFieldName: string = "amount",
): TypedBenefitObject | undefined {
  if (typeValue === undefined || typeValue === null || typeValue === "") {
    return undefined;
  }

  const result: TypedBenefitObject = { type: typeValue };

  const isIncludeAmount = includeAmountWhen.includes(typeValue);
  const isDifferentThanNotDisclosed = typeValue !== NotDisclosed;

  if (
    isDifferentThanNotDisclosed &&
    amountValue !== undefined &&
    amountValue !== null &&
    isIncludeAmount
  ) {
    result[amountFieldName] = amountValue;
  }

  if (
    isIncludeAmount &&
    result[amountFieldName] === undefined &&
    isDifferentThanNotDisclosed
  ) {
    return undefined;
  }

  return result;
}

/**
 * Transforms a type/amount pair from API format to grid format.
 *
 * @param apiObject - The nested API object (e.g., { type: "dollarAmount", amount: 5000 })
 * @param prefix - The field name prefix for the flat fields (e.g., "diagnosisBenefit")
 */
export function transformTypeAmountFromApi(
  apiObject: { type?: string; amount?: number } | null | undefined,
  prefix: string,
): Record<string, unknown> {
  const typeKey = `${prefix}Type`;
  const amountKey = `${prefix}Amount`;

  if (apiObject && typeof apiObject === "object") {
    return {
      [typeKey]: apiObject.type,
      [amountKey]: apiObject.amount,
    };
  }

  return {
    [typeKey]: undefined,
    [amountKey]: undefined,
  };
}
